import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi, zatcaApi } from '@/services/api';
import {
  FileCheck,
  ShieldAlert,
  Save,
  Loader2,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Radio,
  Server,
  Info,
} from 'lucide-react';

export default function ZATCAIntegrationTab() {
  const qc = useQueryClient();
  const [otp, setOtp] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);
  const [onboardResult, setOnboardResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const res = await authApi.getCompanyProfile();
      return res.data.message;
    },
  });

  const [form, setForm] = useState({
    enable_zatca_integration: false,
    zatca_environment: 'Sandbox' as 'Sandbox' | 'Production',
    zatca_vat_number: '',
    zatca_company_name_ar: '',
    zatca_cr_number: '',
    zatca_fail_safe_mode: 'Queue & Retry' as 'Queue & Retry' | 'Strict Block',
  });

  useEffect(() => {
    if (profile) {
      setForm({
        enable_zatca_integration: profile.enable_zatca_integration === 1 || profile.enable_zatca_integration === true,
        zatca_environment: profile.zatca_environment || 'Sandbox',
        zatca_vat_number: profile.zatca_vat_number || profile.tax_registration_id || '',
        zatca_company_name_ar: profile.zatca_company_name_ar || profile.company_name_ar || '',
        zatca_cr_number: profile.zatca_cr_number || '',
        zatca_fail_safe_mode: profile.zatca_fail_safe_mode || 'Queue & Retry',
      });
    }
  }, [profile]);

  const saveMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => authApi.updateCompanyProfile(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      setSaveSuccess(true);
      setSaveError('');
      setTimeout(() => setSaveSuccess(false), 4000);
    },
    onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
      setSaveError(err.response?.data?.message || err.message || 'Failed to save ZATCA settings');
      setSaveSuccess(false);
    },
  });

  const testMutation = useMutation({
    mutationFn: () => zatcaApi.testConnection(),
    onSuccess: (res) => {
      const msg = res.data.message;
      setTestResult({ success: msg.success, message: msg.message || (msg.success ? 'Connection OK' : 'Failed') });
    },
    onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
      setTestResult({ success: false, message: err.response?.data?.message || err.message || 'Test failed' });
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => zatcaApi.retryPendingInvoices(),
    onSuccess: (res) => {
      const msg = res.data.message;
      setSyncResult({ success: msg.success, message: msg.message || `Synced ${msg.synced || 0} invoices` });
    },
    onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
      setSyncResult({ success: false, message: err.response?.data?.message || err.message || 'Sync failed' });
    },
  });

  const onboardMutation = useMutation({
    mutationFn: () => zatcaApi.onboardEgs(otp),
    onSuccess: (res) => {
      const msg = res.data.message;
      setOnboardResult({ success: msg.success !== false, message: msg.message || 'EGS onboarded' });
      qc.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
      setOnboardResult({ success: false, message: err.response?.data?.message || err.message || 'Onboarding failed' });
    },
  });

  const isIncomplete = form.enable_zatca_integration && (!form.zatca_vat_number || !form.zatca_company_name_ar);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if ((profile?.country || '') !== 'SA') {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        ZATCA Fatoora is only available for Saudi Arabia. Set your company country to Saudi Arabia to configure ZATCA.
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="bg-gradient-to-r from-emerald-700 to-emerald-900 text-white rounded-xl p-5 flex items-center gap-4">
        <FileCheck className="w-10 h-10 shrink-0 opacity-90" />
        <div>
          <h2 className="text-lg font-bold">ZATCA Fatoora Phase 2</h2>
          <p className="text-emerald-100 text-sm">Saudi e-invoicing — simplified tax invoices with TLV QR</p>
          <div className="flex gap-2 mt-2">
            <span className={`text-xs px-2 py-0.5 rounded-full ${form.enable_zatca_integration ? 'bg-green-400/30' : 'bg-white/20'}`}>
              {form.enable_zatca_integration ? 'Enabled' : 'Disabled'}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-white/20">{form.zatca_environment}</span>
          </div>
        </div>
      </div>

      {isIncomplete && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold">Setup incomplete</p>
            <p>VAT/TRN and Arabic seller name are required when ZATCA is enabled.</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.enable_zatca_integration}
            onChange={(e) => setForm({ ...form, enable_zatca_integration: e.target.checked })}
            className="w-5 h-5 rounded border-gray-300 text-emerald-600"
          />
          <span className="font-medium text-gray-900">Enable ZATCA Integration</span>
        </label>

        <div className="grid grid-cols-2 gap-3">
          {(['Sandbox', 'Production'] as const).map((env) => (
            <button
              key={env}
              type="button"
              onClick={() => setForm({ ...form, zatca_environment: env })}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                form.zatca_environment === env ? 'border-emerald-600 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {env === 'Sandbox' ? <Radio className="w-5 h-5 mb-1 text-emerald-600" /> : <Server className="w-5 h-5 mb-1 text-emerald-600" />}
              <div className="font-semibold">{env}</div>
              <div className="text-xs text-gray-500">{env === 'Sandbox' ? 'Developer portal / testing' : 'Live Fatoora reporting'}</div>
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">VAT / TRN Number</label>
            <input className="input-field" value={form.zatca_vat_number}
              onChange={(e) => setForm({ ...form, zatca_vat_number: e.target.value })} placeholder="3XXXXXXXXXXXXX3" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CR Number</label>
            <input className="input-field" value={form.zatca_cr_number}
              onChange={(e) => setForm({ ...form, zatca_cr_number: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Seller Name (Arabic)</label>
            <input className="input-field" dir="rtl" value={form.zatca_company_name_ar}
              onChange={(e) => setForm({ ...form, zatca_company_name_ar: e.target.value })} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Fail-Safe Strategy</label>
          <div className="space-y-2">
            {(['Queue & Retry', 'Strict Block'] as const).map((mode) => (
              <label key={mode} className="flex items-start gap-2 cursor-pointer">
                <input type="radio" name="zatca_fail_safe" checked={form.zatca_fail_safe_mode === mode}
                  onChange={() => setForm({ ...form, zatca_fail_safe_mode: mode })} className="mt-1" />
                <div>
                  <span className="font-medium text-sm">{mode}</span>
                  <p className="text-xs text-gray-500">
                    {mode === 'Queue & Retry' ? 'Allow sale; retry ZATCA reporting in background' : 'Block sale if ZATCA reporting fails'}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {profile?.zatca_icv_counter != null && (
          <div className="text-xs text-gray-500 flex gap-4">
            <span>ICV counter: {profile.zatca_icv_counter}</span>
            {profile.zatca_last_invoice_hash && <span className="truncate">Last hash: {String(profile.zatca_last_invoice_hash).slice(0, 16)}…</span>}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Info className="w-4 h-4" /> EGS Onboarding (OTP)
        </h3>
        <div className="flex gap-2">
          <input className="input-field flex-1" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="OTP from ZATCA Fatoora portal" />
          <button type="button" onClick={() => onboardMutation.mutate()} disabled={!otp || onboardMutation.isPending}
            className="btn-secondary whitespace-nowrap">
            {onboardMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Onboard EGS'}
          </button>
        </div>
        {onboardResult && (
          <div className={`text-sm p-3 rounded-lg ${onboardResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {onboardResult.message}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={() => testMutation.mutate()} disabled={testMutation.isPending}
          className="btn-secondary flex items-center gap-2">
          {testMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          Test Configuration
        </button>
        <button type="button" onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}
          className="btn-secondary flex items-center gap-2">
          {syncMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Sync Queued Invoices
        </button>
        <button type="button" onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}
          className="btn-primary flex items-center gap-2 ml-auto">
          {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save ZATCA Settings
        </button>
      </div>

      {testResult && (
        <div className={`p-4 rounded-lg flex items-start gap-2 ${testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {testResult.success ? <CheckCircle className="w-5 h-5 shrink-0" /> : <AlertTriangle className="w-5 h-5 shrink-0" />}
          <span className="text-sm">{testResult.message}</span>
        </div>
      )}
      {syncResult && (
        <div className={`p-4 rounded-lg text-sm ${syncResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {syncResult.message}
        </div>
      )}
      {saveSuccess && (
        <div className="p-4 rounded-lg bg-green-50 text-green-800 text-sm flex items-center gap-2">
          <CheckCircle className="w-5 h-5" /> ZATCA settings saved
        </div>
      )}
      {saveError && (
        <div className="p-4 rounded-lg bg-red-50 text-red-800 text-sm">{saveError}</div>
      )}
    </div>
  );
}
